export const PARAMETERS = [
  { key: "temperature", title: "Temperature", unit: "°C", short: "Temp" },
  { key: "current", title: "Current", unit: "A", short: "Current" },
  { key: "time", title: "Hold Time", unit: "s", short: "Time" },
];
export const PARAMETER_COLORS = { ok: "#0f8069", control: "#ffb400", spec: "#ff4b45", unknown: "#94a3b8" };
export const isReading = value => typeof value === "number" && Number.isFinite(value);
export const hasLimits = limits => [limits?.lsl, limits?.lcl, limits?.ucl, limits?.usl].some(isReading);
export const classifyReading = (value, limits) => {
  if (!isReading(value) || !hasLimits(limits)) return "unknown";
  if ((isReading(limits.lsl) && value < limits.lsl) || (isReading(limits.usl) && value > limits.usl)) return "spec";
  if ((isReading(limits.lcl) && value < limits.lcl) || (isReading(limits.ucl) && value > limits.ucl)) return "control";
  return "ok";
};
export const limitsFor = (record, key, fallback = {}) => {
  // An explicit empty snapshot means limits were unavailable at capture time.
  if (record.raw && Object.prototype.hasOwnProperty.call(record.raw, "limits")) return record.limits?.[key];
  return record.limits?.[key] ?? fallback[key];
};

/** Each recorded reading belongs to one bucket, including values at the maximum. */
export const buildDistribution = (records, key, limits = {}) => {
  const readings = records.filter(record => isReading(record[key]));
  if (!readings.length) return { bins: [], total: 0, counts: { ok: 0, control: 0, spec: 0, unknown: 0 } };
  const allLimits = readings.map(record => limitsFor(record, key, limits));
  const signatures = new Set(allLimits.map(value => JSON.stringify(value || {})));
  const commonLimits = signatures.size === 1 ? allLimits[0] : null;
  const values = readings.map(record => record[key]);
  const bounds = [...values, ...Object.values(commonLimits || {}).filter(isReading)];
  let min = Math.min(...bounds), max = Math.max(...bounds);
  if (min === max) { const padding = Math.max(1, Math.abs(min) * 0.02); min -= padding; max += padding; }
  const edges = [...new Set([min, max, ...Array.from({ length: 7 }, (_, i) => min + (max - min) * (i + 1) / 8), ...Object.values(commonLimits || {}).filter(isReading)])].sort((a, b) => a - b);
  const emptyCounts = () => ({ ok: 0, control: 0, spec: 0, unknown: 0 });
  const bins = edges.slice(0, -1).map((from, index) => ({ from, to: edges[index + 1], count: 0, counts: emptyCounts() }));
  const counts = emptyCounts();
  readings.forEach(record => {
    const value = record[key];
    const index = Math.max(0, bins.findIndex((bin, i) => value >= bin.from && (value < bin.to || i === bins.length - 1)));
    const classification = classifyReading(value, limitsFor(record, key, limits));
    bins[index].count += 1; bins[index].counts[classification] += 1; counts[classification] += 1;
  });
  return { bins, total: readings.length, counts, min, max, commonLimits, varyingLimits: signatures.size > 1 };
};
